import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/lib/LangContext";

// Photo per category (null = text-only, editorial asymmetric)
// 0 Carne, 1 Verdure, 2 Salse, 3 Brodi, 4 Fondi, 5 Basi, 6 Semilavorati, 7 Piatti pronti
const CATEGORY_IMAGES = [
  "https://images.unsplash.com/photo-1544025162-d76694265947?auto=format&fit=crop&w=1200&q=85",
  "https://images.unsplash.com/photo-1466637574441-749b8f19452f?auto=format&fit=crop&w=1200&q=85",
  null, // Salse
  null, // Brodi
  null, // Fondi
  "https://images.unsplash.com/photo-1509440159596-0249088772ff?auto=format&fit=crop&w=1200&q=85",
  null, // Semilavorati
  "https://images.unsplash.com/photo-1607330289024-1535c6b4e1c1?auto=format&fit=crop&w=1200&q=85",
];

export default function WhatWeMake() {
  const { t } = useLang();
  return (
    <section id="make" className="relative py-28 lg:py-40" data-testid="make-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-6">
            <div className="text-[11px] uppercase tracking-[0.28em] text-red-400 font-mono-ef">
              — {t.make.overline}
            </div>
            <h2 className="mt-6 font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-[-0.035em]">
              {t.make.title}
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 lg:pt-4">
            <p className="text-lg text-white/60 leading-relaxed">{t.make.subtitle}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {t.make.list.map((it, i) => {
            const img = CATEGORY_IMAGES[i];
            return (
              <motion.div
                key={it.t}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.05, duration: 0.6 }}
                className="border-t border-l border-white/10 hover:bg-white/[0.02] transition-colors group flex flex-col"
                data-testid={`make-${it.t.toLowerCase()}`}
              >
                {img ? (
                  <div className="relative overflow-hidden aspect-[4/3] bg-neutral-900">
                    <img
                      src={img}
                      alt={it.t}
                      loading="lazy"
                      className="w-full h-full object-cover grayscale-[0.35] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700 ease-out"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute top-4 left-4 font-mono-ef text-[10px] uppercase tracking-[0.24em] text-white/80 bg-black/50 backdrop-blur-md border border-white/15 rounded-full px-2 py-1">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                  </div>
                ) : (
                  <div className="relative aspect-[4/3] flex items-center justify-center bg-[#0a0a0c] overflow-hidden">
                    <span className="chapter-num text-[10rem] lg:text-[13rem] leading-none opacity-90">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="absolute inset-0 pointer-events-none" style={{
                      backgroundImage: "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
                      backgroundSize: "40px 40px",
                    }} />
                  </div>
                )}
                <div className="p-6 lg:p-8 flex-1">
                  <h3 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-white group-hover:text-red-400 transition-colors">
                    {it.t}
                  </h3>
                  <p className="mt-3 text-sm text-white/55 leading-relaxed">{it.d}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
        <div className="border-b border-r border-white/10 h-px w-full" />
      </div>
    </section>
  );
}
